import { useRef, useState, useEffect } from 'react';
import { Bold, Italic, Underline, Heading2, Link as LinkIcon, List, ListOrdered, Image as ImageIcon, Eye, Code2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HtmlContent } from '@/components/common/HtmlContent';

/**
 * Simple rich text editor for ad descriptions.
 * - Toolbar with basic formatting.
 * - Uses contentEditable + document.execCommand (deprecated but works cross-browser for MVP).
 * - Preview tab renders sanitized output.
 * - "HTML" tab lets power users paste custom HTML.
 */
export const HtmlEditor = ({ value, onChange, rows = 8, placeholder = 'Describe con detalle…', dataTestId = 'html-editor' }) => {
    const ref = useRef(null);
    const [mode, setMode] = useState('rich'); // rich | html | preview
    const [raw, setRaw] = useState(value || '');

    useEffect(() => { setRaw(value || ''); }, [value]);

    useEffect(() => {
        if (mode === 'rich' && ref.current && ref.current.innerHTML !== (value || '')) {
            ref.current.innerHTML = value || '';
        }
    }, [mode, value]);

    const emit = (v) => {
        setRaw(v);
        onChange(v);
    };

    const exec = (cmd, arg = null) => {
        document.execCommand(cmd, false, arg);
        if (ref.current) emit(ref.current.innerHTML);
    };

    const handleLink = () => {
        const url = window.prompt('URL:', 'https://');
        if (url) exec('createLink', url);
    };

    const handleImage = () => {
        const url = window.prompt('URL de imagen:', 'https://');
        if (url) exec('insertImage', url);
    };

    const ToolBtn = ({ onClick, icon: Icon, label, testId }) => (
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} className="p-1.5 rounded hover:bg-slate-200 text-slate-700" title={label} data-testid={testId}>
            <Icon className="h-4 w-4" />
        </button>
    );

    return (
        <div className="border rounded-lg" data-testid={dataTestId}>
            <div className="flex items-center gap-1 border-b bg-slate-50 px-2 py-1 flex-wrap">
                {mode === 'rich' && (
                    <>
                        <ToolBtn onClick={() => exec('bold')} icon={Bold} label="Negrita" testId="rich-bold" />
                        <ToolBtn onClick={() => exec('italic')} icon={Italic} label="Cursiva" testId="rich-italic" />
                        <ToolBtn onClick={() => exec('underline')} icon={Underline} label="Subrayado" testId="rich-underline" />
                        <span className="w-px h-4 bg-slate-300 mx-1" />
                        <ToolBtn onClick={() => exec('formatBlock', '<h3>')} icon={Heading2} label="Título" testId="rich-h3" />
                        <ToolBtn onClick={() => exec('insertUnorderedList')} icon={List} label="Lista" testId="rich-ul" />
                        <ToolBtn onClick={() => exec('insertOrderedList')} icon={ListOrdered} label="Lista numerada" testId="rich-ol" />
                        <span className="w-px h-4 bg-slate-300 mx-1" />
                        <ToolBtn onClick={handleLink} icon={LinkIcon} label="Enlace" testId="rich-link" />
                        <ToolBtn onClick={handleImage} icon={ImageIcon} label="Imagen" testId="rich-image" />
                    </>
                )}
                <div className="ml-auto flex items-center gap-1">
                    <Button type="button" size="sm" variant={mode === 'rich' ? 'default' : 'ghost'} className="h-7 px-2" onClick={() => setMode('rich')} data-testid="mode-rich">Editor</Button>
                    <Button type="button" size="sm" variant={mode === 'html' ? 'default' : 'ghost'} className="h-7 px-2" onClick={() => setMode('html')} data-testid="mode-html"><Code2 className="h-3.5 w-3.5 mr-1" />HTML</Button>
                    <Button type="button" size="sm" variant={mode === 'preview' ? 'default' : 'ghost'} className="h-7 px-2" onClick={() => setMode('preview')} data-testid="mode-preview"><Eye className="h-3.5 w-3.5 mr-1" />Vista</Button>
                </div>
            </div>

            {mode === 'rich' && (
                <div
                    ref={ref}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={() => ref.current && emit(ref.current.innerHTML)}
                    className="min-h-[160px] p-3 outline-none prose prose-sm max-w-none focus:ring-2 focus:ring-primary/20 rounded-b-lg"
                    style={{ minHeight: `${rows * 20}px` }}
                    data-testid="rich-content"
                    data-placeholder={placeholder}
                />
            )}

            {mode === 'html' && (
                <textarea
                    className="w-full p-3 font-mono text-xs outline-none focus:ring-2 focus:ring-primary/20 rounded-b-lg"
                    style={{ minHeight: `${rows * 20}px` }}
                    value={raw}
                    placeholder="<p>Escribe HTML aquí…</p>"
                    onChange={(e) => emit(e.target.value)}
                    data-testid="html-textarea"
                />
            )}

            {mode === 'preview' && (
                <div className="p-3 min-h-[160px]" data-testid="preview-content">
                    {raw ? <HtmlContent html={raw} /> : <span className="text-sm text-muted-foreground">Sin contenido para mostrar.</span>}
                </div>
            )}

            <div className="border-t px-2 py-1 text-[10px] text-muted-foreground bg-slate-50 rounded-b-lg">
                Se permiten estilos básicos y tablas. El HTML se sanitiza automáticamente para tu seguridad.
            </div>
        </div>
    );
};

export default HtmlEditor;
